/**
 * hooks/useMergeHistoryForClient.ts
 *
 * Loads the merge history entries for a specific client (as target or source).
 * Used by ClientHistoryTab (timeline events) and ClientInfoTab (previous value indicators).
 * Created Apr 2026.
 * Fix Apr 2026: getPreviousValuesForTarget now returns FieldPreviousValue per field
 *   (value + mergedAt + sourceSnapshot) so callers show the correct merge date and
 *   source name per field instead of reusing the first merge entry for all fields.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { captureError } from '../utils/errorReporting';
import type { MergeHistoryEntry, FieldChange, ClientSnapshot } from '../types/merge';

export interface FieldPreviousValue {
  value: string | null;
  mergedAt: string;
  sourceSnapshot: ClientSnapshot | null;
}

interface UseMergeHistoryForClientParams {
  clientId: string | undefined;
  userId: string | undefined;
}

export function useMergeHistoryForClient({ clientId, userId }: UseMergeHistoryForClientParams) {
  const [mergeHistory, setMergeHistory] = useState<MergeHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!clientId || !userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('client_merge_history')
        .select('*')
        .eq('user_id', userId)
        .or(`target_client_id.eq.${clientId},source_client_id.eq.${clientId}`)
        .order('merged_at', { ascending: false });

      if (error) throw error;

      const entries: MergeHistoryEntry[] = (data || []).map((row) => ({
        id: row.id,
        source_client_id: row.source_client_id,
        target_client_id: row.target_client_id,
        user_id: row.user_id,
        merged_at: row.merged_at,
        can_undo_until: row.can_undo_until,
        source_snapshot: row.source_snapshot as MergeHistoryEntry['source_snapshot'],
        target_client_snapshot_before: row.target_client_snapshot_before as MergeHistoryEntry['target_client_snapshot_before'],
        field_changes: (row.field_changes as FieldChange[]) || [],
        reassigned_request_ids: row.reassigned_request_ids || [],
      }));

      setMergeHistory(entries);
    } catch (err) {
      captureError(err, { hook: 'useMergeHistoryForClient', action: 'fetchHistory', clientId });
    } finally {
      setLoading(false);
    }
  }, [clientId, userId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  /**
   * Returns per-field previous values for a client that was the merge TARGET.
   * Each entry carries the old value, the merge timestamp, and the source snapshot
   * so callers can show the correct provenance per field.
   */
  const getPreviousValuesForTarget = useCallback(
    (targetClientId: string): Record<string, FieldPreviousValue> => {
      const result: Record<string, FieldPreviousValue> = {};
      for (const entry of mergeHistory) {
        if (entry.target_client_id !== targetClientId) continue;
        for (const change of entry.field_changes) {
          if (!(change.field in result)) {
            result[change.field] = {
              value: change.old_value,
              mergedAt: entry.merged_at,
              sourceSnapshot: entry.source_snapshot,
            };
          }
        }
      }
      return result;
    },
    [mergeHistory]
  );

  return { mergeHistory, loading, refresh: fetchHistory, getPreviousValuesForTarget };
}
